import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { ExternalLink, Github } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface Project {
  id: number;
  title: string;
  description: string;
  technologies: string[];
  imageUrl: string;
  liveUrl: string;
  liveUrl1: string;
  githubUrl: string;
}

export const ProjectsSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  // Sample projects data (replace with real data)
  const projectsData: Project[] = [
    {
      id: 1,
      title: "E-commerce Platforms",
      description: t("projects.data.1.description"),
      technologies: [
        "Web Component",
        "HMC",
        "React JS",
        "Vue JS",
        "Opencart",
        "PHP",
        "MySQL",
        "JQuery",
        "API Integration",
      ],
      imageUrl: "/ecommerce.webp",
      liveUrl: "https://nespresso.com/",
      liveUrl1: "https://www.herbacos.be/",
      githubUrl: "",
    },
    {
      id: 2,
      title: "Various Project",
      description: t("projects.data.2.description"),
      technologies: [
        "Opencart",
        "Wordpress",
        "AWS",
        "Hosting",
        "Cloud",
        "API Integration",
        "Cyber Security",
      ],
      imageUrl: "/meninblue.webp",
      liveUrl: "https://drshahsayurveda.com/",
      liveUrl1: "https://mibcricket.com/",
      githubUrl: "",
    },
    {
      id: 3,
      title: "Android Apps",
      description: t("projects.data.3.description"),
      technologies: ["Java", "XML", "Android Studio", "Kotlin", "React Native"],
      imageUrl: "/android_ayurveda.webp",
      liveUrl: "https://play.google.com/store/apps/developer?id=Aditya+Kandele",
      liveUrl1: "",
      githubUrl: "",
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="py-20 px-6 md:px-12 lg:px-24 relative"
    >
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6 }}
        className="text-3xl font-bold mb-16 text-center"
      >
        {t("projects.title")}
      </motion.h2>

      <motion.div
        variants={container}
        initial="hidden"
        animate={isInView ? "show" : "hidden"}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
      >
        {projectsData.map((project) => (
          <motion.div key={project.id} variants={item}>
            <Card className="overflow-hidden h-full flex flex-col transition-transform duration-300 hover:scale-105 border border-accent/20 bg-secondary/20 backdrop-blur-sm">
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  src={project.imageUrl}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <CardHeader>
                <CardTitle>{project.title}</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <p className="text-muted-foreground mb-4">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs rounded-full bg-accent/10 text-accent"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                {project.liveUrl != "" ? (
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t("projects.viewLive")}
                    </a>
                  </Button>
                ) : (
                  ""
                )}
                {project.liveUrl1 != "" ? (
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={project.liveUrl1}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t("projects.viewLive")}
                    </a>
                  </Button>
                ) : (
                  ""
                )}
                {project.githubUrl != "" ? (
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Github className="h-4 w-4 mr-2" />
                      {t("projects.viewCode")}
                    </a>
                  </Button>
                ) : (
                  ""
                )}
              </CardFooter>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};
